import {
  Smartphone, Zap, RefreshCw, ShieldCheck,
  Gem, Sparkles, CreditCard, Users, Truck, BarChart3,
} from "lucide-react";
import goldHero from "@/assets/products/gold-hero.webp";
import goldFeatures from "@/assets/products/gold-features.webp";
import goldTeam from "@/assets/products/gold-team-control.webp";
import goldProfile from "@/assets/products/gold-customise-profile.webp";
import type { ProductPageDict } from "@/lib/shop/product-dict";

const SLUG = "24kt-gold";
const SKU = "BC-AU24";
const BASE_PRICE = 108;
const PRODUCT_URL_EN = "https://banana.cards/shop/24kt-gold";
const PRODUCT_URL_VI = "https://banana.cards/vi/shop/24kt-gold";
const RATING = { value: "4.92", count: "47" };

const BUNDLES = [
  { qty: 1,  label: "1 card",  perCard: 1.0 },
  { qty: 3,  label: "3 cards", perCard: 0.9,  save: 10, badge: "Save 10%" },
  { qty: 5,  label: "5 cards", perCard: 0.82, save: 18, badge: "Most popular" },
  { qty: 10, label: "10 cards", perCard: 0.72, save: 28, badge: "Best value" },
] as const;

const BUNDLES_VI = [
  { qty: 1,  label: "1 thẻ",  perCard: 1.0 },
  { qty: 3,  label: "3 thẻ",  perCard: 0.9,  save: 10, badge: "Tiết kiệm 10%" },
  { qty: 5,  label: "5 thẻ",  perCard: 0.82, save: 18, badge: "Phổ biến nhất" },
  { qty: 10, label: "10 thẻ", perCard: 0.72, save: 28, badge: "Giá trị nhất" },
] as const;

const GALLERY = [
  { src: goldHero,     alt: "Custom 24kt Gold AI business card front and back with NFC and QR" },
  { src: goldFeatures, alt: "24kt Gold card features — engraved logo, NFC chip, QR code" },
  { src: goldProfile,  alt: "Customizable digital profiles on iPhone" },
  { src: goldTeam,     alt: "Team & enterprise control dashboard" },
];

const GALLERY_VI = [
  { src: goldHero,     alt: "Thẻ vàng 24kt AI mặt trước và sau với NFC và mã QR" },
  { src: goldFeatures, alt: "Cấu tạo thẻ vàng 24kt — logo khắc, chip NFC, mã QR" },
  { src: goldProfile,  alt: "Hồ sơ kỹ thuật số tùy chỉnh trên iPhone" },
  { src: goldTeam,     alt: "Bảng điều khiển đội nhóm và doanh nghiệp" },
];

export const goldDict: ProductPageDict = {
  slug: SLUG,
  routePath: "/shop/24kt-gold",
  productUrl: PRODUCT_URL_EN,
  basePrice: BASE_PRICE,
  sku: SKU,
  rating: RATING,
  seo: {
    title: "Custom 24kt Gold AI Business Card — banana.cards",
    description: "24-karat gold-plated NFC business card with deep-engraved logo, embedded NFC chip and QR code. The ultimate luxury card with AI-powered follow-ups.",
    ogTitle: "Custom 24kt Gold AI Business Card",
    ogDescription: "24kt gold-plated NFC business card. Make every introduction unforgettable — AI follow-ups built in.",
  },
  jsonLdDescription:
    "The Custom 24kt Gold AI Business Card features a precision-cut stainless steel core finished in 24-karat gold plating, with deep-engraved branding, an embedded NFC chip and a printed QR code linked to your banana.cards profile.",
  eyebrow: "banana.cards · 24kt Gold",
  productName: "Custom 24kt Gold AI Business Card",
  productIntro:
    "The ultimate statement of status. A precision-cut stainless steel core finished in 24-karat gold plating with deep-engraved branding — weighted, luminous and unforgettable. Embedded NFC and a printed QR turn every introduction into a captured, AI-followed-up contact.",
  ratingLabel: "4.92 · 47 reviews",
  perCardSuffix: "/ card · Free worldwide shipping over $50",
  saveBadge: (pct) => `Save ${pct}%`,
  bundles: BUNDLES,
  bundleSection: {
    eyebrow: "Bundle & save",
    heading: { pre: "Equip your team in", em: "solid gold." },
    sub: "Each card carries its own profile — perfect for founders, executives and high-touch sales teams.",
    bundleLabel: "Bundle & save",
    perCardLabel: "per card",
    selected: "Selected",
    choose: "Choose",
  },
  usps: [
    { icon: Smartphone, label: "Share your details with one tap" },
    { icon: Zap,        label: "Collect their details back with one click" },
    { icon: RefreshCw,  label: "No app needed — works on both Apple & Android" },
    { icon: ShieldCheck,label: "Lifetime hardware guarantee" },
  ],
  features: {
    eyebrow: "Why 24kt gold",
    heading: { pre: "Luxurious to hold.", em: "Smart to use." },
    items: [
      { icon: Gem,        title: "24kt gold plating",         desc: "Real 24-karat gold electroplated onto a precision stainless steel core — heavyweight, luminous, scratch-resistant." },
      { icon: Sparkles,   title: "AI follow-ups built-in",    desc: "Every tap lands in your banana CRM, enriched and ready for AI-drafted follow-ups." },
      { icon: CreditCard, title: "Deep-engraved branding",    desc: "Your logo, name and details engraved permanently into the metal core — visible through the plating, unmistakably premium." },
      { icon: Users,      title: "Founder & exec tier",       desc: "The card that turns heads in boardrooms — built for the people who close the biggest deals." },
      { icon: Truck,      title: "Free worldwide shipping",   desc: "Orders over $50 ship free to 100+ countries — 3-5 business days." },
      { icon: BarChart3,  title: "Real-time analytics",       desc: "See every tap, click and lead in your dashboard — know which events actually convert." },
    ],
  },
  anatomy: {
    eyebrow: "Card anatomy",
    heading: { pre: "Every detail,", em: "engineered." },
    image: goldFeatures,
    imageAlt: "Anatomy of the 24kt Gold AI business card — engraved logo, NFC chip and QR code",
    items: [
      { t: "24kt gold-plated surface", d: "Real 24-karat gold electroplated to a deep, luminous finish — built to last and rich to the touch." },
      { t: "Stainless steel core",     d: "Precision-cut steel underneath the gold — gives the card its premium weight and bend-resistance." },
      { t: "Embedded NFC chip",        d: "Tap to share your full profile to any modern iPhone or Android — no app required." },
      { t: "Printed QR code",          d: "A perfect fallback for older devices or thicker phone cases — every introduction lands." },
    ],
  },
  profile: {
    eyebrow: "Your profile, your way",
    heading: { pre: "One card.", em: "Infinite identities." },
    body: "Pick a layout, add your colors, photo, links, services and CTAs — and switch between work, investor or VIP profiles from the same gold card.",
    bullets: [
      "12+ designer-made layouts & themes",
      "Add video, gallery, services, FAQs",
      "Multi-language profiles",
      "Lead-capture form on every card",
    ],
    image: goldProfile,
    imageAlt: "Customisable digital profiles on iPhone",
  },
  compare: {
    eyebrow: "Paper vs Gold",
    heading: { pre: "Paper cards get lost.", em: "Gold gets framed." },
    sub: "You deserve something that actually works — and that people actually keep.",
    paperLabel: "Paper business cards",
    productLabel: "Custom 24kt Gold AI Business Card",
    paper: [
      "Hand out paper cards and hope they keep it",
      "Manually follow up days later",
      "Forget who you met or what you said",
      "No analytics, no CRM, no visibility",
      "You look like everyone else",
    ],
    product: [
      "Tap once, your details save instantly",
      "Automatic smart follow-ups",
      "Every contact captured automatically",
      "Real-time analytics show which events convert",
      "Luxurious, elite, unforgettable",
    ],
  },
  steps: {
    eyebrow: "How it works",
    heading: { pre: "You don't need to explain.", em: "You just tap." },
    sub: "They get everything that matters — instantly.",
    stepLabel: (n) => `Step ${n}`,
    items: [
      { title: "Tap once to share easily",   desc: "Tap your NFC Banana Card on any smartphone, no app required. Your profile, links and calendar open instantly." },
      { title: "They save you instantly",    desc: "They add you straight to their contacts — your info stays where they actually look: their phone." },
      { title: "You get their details back", desc: "Each new lead lands automatically in your Banana dashboard and syncs to your CRM — no manual entry." },
    ],
  },
  team: {
    eyebrow: "For teams",
    heading: { pre: "Built for", em: "founders and execs." },
    body: "Roll out gold cards for your executive team or VIP partners, manage every profile from one dashboard, and route every new contact straight into your shared CRM.",
    cta: "Order for your team",
    image: goldTeam,
    imageAlt: "Team and enterprise control dashboard",
    teamsHref: "/teams",
  },
  faq: {
    eyebrow: "FAQ",
    heading: { pre: "Frequently asked", em: "questions." },
    items: [
      { q: "Is the gold real 24-karat gold?", a: "Yes. The card is electroplated with real 24-karat gold over a precision-cut stainless steel core — giving it the depth, color and luminosity of solid gold while keeping the structural rigidity of steel." },
      { q: "How thick is the gold layer?", a: "Our electroplating spec produces a finish thick enough to resist standard scratching and wear — you can carry the card daily without worrying about the plating wearing thin." },
      { q: "How do I customize my gold card?", a: "After checkout we send you a short design brief. Upload your logo, pick what you want engraved — our team mocks it up, you approve, we produce. Most cards ship within 7-10 business days of design approval." },
      { q: "Does the gold card work through phone cases?", a: "Yes. The embedded NFC antenna is tuned to read through most silicone, leather and plastic cases. For very thick or metal-backed cases, you can also share via the printed QR code." },
      { q: "What digital features come with the gold card?", a: "Every card includes a banana.cards profile: unlimited links, multi-language profiles, lead capture form, Apple & Google Wallet pass, real-time analytics, CRM sync and access to all our AI tools." },
      { q: "How does the AI follow-up feature work?", a: "When someone taps your card, their details are enriched (company, role, signals) and pushed into your dashboard. Our AI drafts a personalized follow-up based on the meeting context — you review, edit and send." },
      { q: "Is the engraving permanent?", a: "Absolutely. The engraving is etched directly into the steel core beneath the gold plating — it's part of the card itself and is covered by our lifetime hardware guarantee." },
      { q: "Can I use the gold card with my existing CRM?", a: "Yes. Banana syncs natively with HubSpot, Salesforce, Pipedrive, Notion, Google Contacts and 50+ tools via Zapier." },
      { q: "Do I need batteries or to charge the card?", a: "No. The NFC chip is passive — nothing to charge, ever." },
      { q: "Is there a digital backup if I don't have the physical card?", a: "Yes. Your profile lives at a unique URL — share via link, QR, email signature or Apple/Google Wallet pass." },
    ],
  },
  outro: {
    heading: { pre: "Network with", em: "gold in your hand." },
    body: "Order before 3pm and we ship the next business day. Free worldwide shipping over $50.",
    primaryCta: "Get my Gold card",
    secondaryCta: "See all products",
    shopHref: "/shop",
  },
  buyBox: { addToCart: "Add to cart" },
  gallery: GALLERY,
};

export const goldDictVi: ProductPageDict = {
  ...goldDict,
  routePath: "/vi/shop/24kt-gold",
  productUrl: PRODUCT_URL_VI,
  seo: {
    title: "Thẻ Danh Thiếp Vàng 24kt AI Tùy Chỉnh — banana.cards",
    description: "Thẻ NFC mạ vàng 24 karat với logo khắc sâu, chip NFC tích hợp và mã QR. Thẻ sang trọng nhất với AI theo dõi tự động.",
    ogTitle: "Thẻ Danh Thiếp Vàng 24kt AI Tùy Chỉnh",
    ogDescription: "Thẻ NFC mạ vàng 24kt. Biến mọi cuộc gặp thành dấu ấn khó quên — tích hợp AI theo dõi.",
    locale: "vi_VN",
  },
  jsonLdDescription:
    "Thẻ Danh Thiếp Vàng 24kt AI Tùy Chỉnh có lõi thép không gỉ cắt chính xác hoàn thiện bằng mạ vàng 24 karat, với thương hiệu khắc sâu, chip NFC tích hợp và mã QR liên kết với hồ sơ banana.cards.",
  eyebrow: "banana.cards · Vàng 24kt",
  productName: "Thẻ Danh Thiếp Vàng 24kt AI Tùy Chỉnh",
  productIntro:
    "Tuyên ngôn đẳng cấp tối thượng. Lõi thép không gỉ cắt chính xác mạ vàng 24 karat với thương hiệu khắc sâu — có trọng lượng, rạng rỡ và khó quên. NFC tích hợp và mã QR in biến mọi cuộc gặp thành một liên hệ được thu thập và AI theo dõi.",
  ratingLabel: "4,92 · 47 đánh giá",
  perCardSuffix: "/ thẻ · Miễn phí vận chuyển toàn cầu cho đơn trên $50",
  saveBadge: (pct) => `Tiết kiệm ${pct}%`,
  bundles: BUNDLES_VI,
  bundleSection: {
    eyebrow: "Mua combo, tiết kiệm",
    heading: { pre: "Trang bị cho đội bằng", em: "vàng nguyên khối." },
    sub: "Mỗi thẻ có hồ sơ riêng — hoàn hảo cho nhà sáng lập, lãnh đạo và đội bán hàng cao cấp.",
    bundleLabel: "Mua combo, tiết kiệm",
    perCardLabel: "mỗi thẻ",
    selected: "Đã chọn",
    choose: "Chọn",
  },
  usps: [
    { icon: Smartphone, label: "Chia sẻ thông tin chỉ với một cú chạm" },
    { icon: Zap,        label: "Thu lại thông tin của họ chỉ với một cú nhấp" },
    { icon: RefreshCw,  label: "Không cần ứng dụng — hoạt động trên cả iPhone và Android" },
    { icon: ShieldCheck,label: "Bảo hành phần cứng trọn đời" },
  ],
  features: {
    eyebrow: "Vì sao chọn vàng 24kt",
    heading: { pre: "Cảm giác sang trọng.", em: "Thông minh để dùng." },
    items: [
      { icon: Gem,        title: "Mạ vàng 24kt thật",         desc: "Vàng 24 karat thật mạ điện lên lõi thép không gỉ — nặng tay, rạng rỡ, chống xước." },
      { icon: Sparkles,   title: "AI theo dõi tích hợp",      desc: "Mỗi cú chạm đều được lưu vào CRM banana, làm giàu dữ liệu và sẵn sàng cho AI soạn lời theo dõi." },
      { icon: CreditCard, title: "Thương hiệu khắc sâu",      desc: "Logo, tên và chi tiết được khắc vĩnh viễn vào lõi kim loại — nhìn thấy qua lớp mạ, đẳng cấp không thể nhầm lẫn." },
      { icon: Users,      title: "Tầng nhà sáng lập & lãnh đạo", desc: "Thẻ khiến đầu người xoay trong phòng họp — dành cho người chốt deal lớn nhất." },
      { icon: Truck,      title: "Vận chuyển toàn cầu miễn phí", desc: "Đơn trên $50 miễn phí giao đến hơn 100 quốc gia." },
      { icon: BarChart3,  title: "Phân tích thời gian thực",   desc: "Xem mọi cú chạm, lượt nhấp và khách tiềm năng trên bảng điều khiển." },
    ],
  },
  anatomy: {
    eyebrow: "Cấu tạo thẻ",
    heading: { pre: "Mọi chi tiết,", em: "đều được tinh chỉnh." },
    image: goldFeatures,
    imageAlt: "Cấu tạo Thẻ Vàng 24kt AI — logo khắc, chip NFC và mã QR",
    items: [
      { t: "Bề mặt mạ vàng 24kt",      d: "Vàng 24 karat thật mạ điện cho hoàn thiện sâu, rạng rỡ — bền và sang trọng khi chạm." },
      { t: "Lõi thép không gỉ",        d: "Thép cắt chính xác dưới lớp vàng — cho trọng lượng cao cấp và chống cong." },
      { t: "Chip NFC tích hợp",        d: "Chạm để chia sẻ hồ sơ với mọi iPhone hoặc Android hiện đại — không cần ứng dụng." },
      { t: "Mã QR in sẵn",             d: "Phương án dự phòng hoàn hảo cho thiết bị cũ hoặc ốp lưng dày." },
    ],
  },
  profile: {
    eyebrow: "Hồ sơ của bạn, theo cách của bạn",
    heading: { pre: "Một thẻ.", em: "Vô vàn bản dạng." },
    body: "Chọn bố cục, thêm màu sắc, ảnh, liên kết, dịch vụ và CTA — và chuyển đổi giữa hồ sơ công việc, nhà đầu tư hoặc VIP từ cùng một thẻ vàng.",
    bullets: [
      "12+ bố cục & chủ đề thiết kế sẵn",
      "Thêm video, thư viện, dịch vụ, FAQ",
      "Hồ sơ đa ngôn ngữ",
      "Biểu mẫu thu khách tiềm năng trên mọi thẻ",
    ],
    image: goldProfile,
    imageAlt: "Hồ sơ kỹ thuật số tùy chỉnh trên iPhone",
  },
  compare: {
    eyebrow: "Giấy vs Vàng",
    heading: { pre: "Thẻ giấy bị thất lạc.", em: "Vàng được trưng bày." },
    sub: "Bạn xứng đáng có thứ thực sự hoạt động — và người ta thực sự giữ lại.",
    paperLabel: "Thẻ danh thiếp giấy",
    productLabel: "Thẻ Danh Thiếp Vàng 24kt AI Tùy Chỉnh",
    paper: [
      "Phát thẻ giấy và hy vọng họ giữ",
      "Theo dõi thủ công vài ngày sau",
      "Quên mất bạn đã gặp ai và nói gì",
      "Không phân tích, không CRM, không tầm nhìn",
      "Bạn trông giống mọi người khác",
    ],
    product: [
      "Chạm một lần, thông tin được lưu tức thì",
      "Theo dõi thông minh tự động",
      "Mọi liên hệ được thu thập tự động",
      "Phân tích thời gian thực cho biết sự kiện nào chuyển đổi",
      "Sang trọng, đẳng cấp, khó quên",
    ],
  },
  steps: {
    eyebrow: "Cách hoạt động",
    heading: { pre: "Bạn không cần giải thích.", em: "Bạn chỉ cần chạm." },
    sub: "Họ nhận được mọi thứ quan trọng — ngay lập tức.",
    stepLabel: (n) => `Bước ${n}`,
    items: [
      { title: "Chạm một lần để chia sẻ",  desc: "Chạm thẻ Banana NFC vào bất kỳ điện thoại nào, không cần ứng dụng." },
      { title: "Họ lưu bạn tức thì",       desc: "Họ thêm bạn vào danh bạ — thông tin nằm đúng nơi họ thực sự nhìn." },
      { title: "Bạn nhận lại thông tin",   desc: "Mỗi khách tiềm năng tự động vào bảng điều khiển Banana và đồng bộ CRM." },
    ],
  },
  team: {
    eyebrow: "Dành cho đội nhóm",
    heading: { pre: "Xây cho", em: "nhà sáng lập và lãnh đạo." },
    body: "Triển khai thẻ vàng cho đội điều hành hoặc đối tác VIP, quản lý mọi hồ sơ trên một bảng điều khiển.",
    cta: "Đặt cho đội của bạn",
    image: goldTeam,
    imageAlt: "Bảng điều khiển đội và doanh nghiệp",
    teamsHref: "/teams",
  },
  faq: {
    eyebrow: "Câu hỏi thường gặp",
    heading: { pre: "Câu hỏi", em: "thường gặp." },
    items: [
      { q: "Vàng có phải là vàng 24 karat thật không?", a: "Có. Thẻ được mạ điện bằng vàng 24 karat thật trên lõi thép không gỉ cắt chính xác — cho độ sâu, màu và độ rạng rỡ của vàng nguyên khối." },
      { q: "Lớp vàng dày bao nhiêu?", a: "Quy cách mạ điện của chúng tôi tạo lớp hoàn thiện đủ dày để chống xước và mài mòn thông thường — bạn có thể mang thẻ hàng ngày mà không lo lớp mạ mỏng đi." },
      { q: "Làm sao tùy chỉnh thẻ vàng?", a: "Sau khi đặt hàng, chúng tôi gửi bản brief thiết kế. Tải logo, chọn nội dung khắc — đội thiết kế làm mockup, bạn duyệt, chúng tôi sản xuất. Hầu hết thẻ giao trong 7-10 ngày làm việc." },
      { q: "Thẻ vàng có hoạt động qua ốp điện thoại không?", a: "Có. Ăng-ten NFC đọc qua hầu hết ốp silicone, da và nhựa. Với ốp dày hoặc mặt kim loại, có thể chia sẻ qua mã QR in." },
      { q: "Thẻ vàng có tính năng số gì?", a: "Mỗi thẻ bao gồm hồ sơ banana.cards: liên kết không giới hạn, hồ sơ đa ngôn ngữ, biểu mẫu thu khách, thẻ Apple & Google Wallet, phân tích thời gian thực và đồng bộ CRM." },
      { q: "AI theo dõi hoạt động thế nào?", a: "Khi ai đó chạm thẻ, thông tin của họ được làm giàu (công ty, vai trò, tín hiệu) và đẩy vào bảng điều khiển. AI soạn lời theo dõi cá nhân hóa dựa trên ngữ cảnh cuộc gặp — bạn duyệt, chỉnh và gửi." },
      { q: "Khắc có vĩnh viễn không?", a: "Hoàn toàn. Khắc được etch trực tiếp vào lõi thép dưới lớp mạ vàng — là một phần của thẻ và được bảo hành phần cứng trọn đời." },
      { q: "Có thể dùng thẻ vàng với CRM hiện tại không?", a: "Có. Banana đồng bộ với HubSpot, Salesforce, Pipedrive, Notion, Google Contacts và 50+ công cụ qua Zapier." },
      { q: "Có cần pin hoặc sạc thẻ không?", a: "Không. Chip NFC là thụ động — không pin, không sạc." },
      { q: "Có bản sao lưu kỹ thuật số nếu mất thẻ không?", a: "Có. Hồ sơ nằm tại một URL duy nhất — chia sẻ qua liên kết, QR, chữ ký email hoặc thẻ Apple/Google Wallet." },
    ],
  },
  outro: {
    heading: { pre: "Kết nối với", em: "vàng trong tay bạn." },
    body: "Đặt trước 3 giờ chiều, chúng tôi giao vào ngày làm việc kế tiếp. Miễn phí vận chuyển toàn cầu cho đơn trên $50.",
    primaryCta: "Mua thẻ Vàng",
    secondaryCta: "Xem tất cả sản phẩm",
    shopHref: "/shop",
  },
  buyBox: { addToCart: "Thêm vào giỏ" },
  gallery: GALLERY_VI,
};

const PRODUCT_URL_GOLD_EL = "https://banana.cards/el/shop/24kt-gold";
const BUNDLES_GOLD_EL = [
  {
    qty: 1,
    label: "1 κάρτα",
    perCard: 1.0
  },
  {
    qty: 3,
    label: "3 κάρτες",
    perCard: 0.9,
    save: 10,
    badge: "Κέρδος 10%"
  },
  {
    qty: 5,
    label: "5 κάρτες",
    perCard: 0.82,
    save: 18,
    badge: "Δημοφιλέστερο"
  },
  {
    qty: 10,
    label: "10 κάρτες",
    perCard: 0.72,
    save: 28,
    badge: "Καλύτερη τιμή"
  }
] as const;
const GALLERY_GOLD_EL = [
  {
    src: goldHero,
    alt: "Μπροστινή και πίσω όψη εξατομικευμένης χρυσής επαγγελματικής κάρτας 24kt με AI, NFC και QR"
  },
  {
    src: goldFeatures,
    alt: "Χρυσή κάρτα 24kt — χαραγμένο λογότυπο, τσιπ NFC και κωδικός QR"
  },
  {
    src: goldProfile,
    alt: "Προσαρμόσιμα ψηφιακά προφίλ σε iPhone"
  },
  {
    src: goldTeam,
    alt: "Πίνακας ελέγχου για ομάδες και επιχειρήσεις"
  }
];
export const goldDictEl: ProductPageDict = {
  ...goldDict,
  routePath: "/el/shop/24kt-gold",
  productUrl: PRODUCT_URL_GOLD_EL,
  seo: {
    title: "Χρυσή κάρτα 24kt με AI — banana.cards",
    description: "Επίχρυση κάρτα NFC 24kt με χαραγμένο λογότυπο, τσιπ NFC και QR. Πολυτέλεια που ξεχωρίζει, με επανεπικοινωνία μέσω AI.",
    ogTitle: "Εξατομικευμένη χρυσή επαγγελματική κάρτα 24kt με AI",
    ogDescription: "Επίχρυση επαγγελματική κάρτα NFC 24kt. Κάθε γνωριμία αξέχαστη — με ενσωματωμένη επανεπικοινωνία μέσω AI.",
    locale: "el_GR"
  },
  jsonLdDescription: "Η εξατομικευμένη χρυσή επαγγελματική κάρτα 24kt με AI διαθέτει πυρήνα ανοξείδωτου χάλυβα κομμένο με ακρίβεια, επιχρύσωση 24kt, βαθιά χάραξη της εταιρικής σας ταυτότητας, ενσωματωμένο τσιπ NFC και τυπωμένο κωδικό QR που οδηγεί στο προφίλ σας στο banana.cards.",
  eyebrow: "banana.cards · Χρυσός 24kt",
  productName: "Εξατομικευμένη χρυσή επαγγελματική κάρτα 24kt με AI",
  productIntro: "Η απόλυτη έκφραση κύρους. Πυρήνας ανοξείδωτου χάλυβα κομμένος με ακρίβεια, επιχρύσωση 24kt και βαθιά χάραξη της εταιρικής σας ταυτότητας — με βάρος, λάμψη και παρουσία που μένει αξέχαστη. Το ενσωματωμένο NFC και ο τυπωμένος κωδικός QR μετατρέπουν κάθε γνωριμία σε καταγεγραμμένη επαφή, με επανεπικοινωνία μέσω AI.",
  ratingLabel: "4,92 · 47 αξιολογήσεις",
  perCardSuffix: "/ κάρτα · Δωρεάν αποστολή παγκοσμίως για παραγγελίες άνω των $50",
  saveBadge: (pct) => `Κέρδος ${pct}%`,
  bundles: BUNDLES_GOLD_EL,
  bundleSection: {
    eyebrow: "Περισσότερες κάρτες, μεγαλύτερο όφελος",
    heading: {
      pre: "Χαρίστε στην ομάδα σας",
      em: "τη λάμψη του χρυσού."
    },
    sub: "Κάθε κάρτα έχει το δικό της προφίλ — ιδανική για ιδρυτές, στελέχη και ομάδες πωλήσεων που επενδύουν στην προσωπική επαφή.",
    bundleLabel: "Πακέτα με όφελος",
    perCardLabel: "ανά κάρτα",
    selected: "Επιλεγμένο",
    choose: "Επιλογή"
  },
  usps: [
    {
      icon: Smartphone,
      label: "Μοιραστείτε τα στοιχεία σας με ένα άγγιγμα"
    },
    {
      icon: Zap,
      label: "Λάβετε και τα δικά τους στοιχεία με ένα κλικ"
    },
    {
      icon: RefreshCw,
      label: "Χωρίς εφαρμογή — λειτουργεί σε Apple και Android"
    },
    {
      icon: ShieldCheck,
      label: "Εγγύηση εφ’ όρου ζωής για την κάρτα"
    }
  ],
  features: {
    eyebrow: "Γιατί χρυσός 24kt",
    heading: {
      pre: "Πολυτέλεια στο χέρι.",
      em: "Ευφυΐα στη χρήση."
    },
    items: [
      {
        icon: Gem,
        title: "Επιχρύσωση 24kt",
        desc: "Πραγματικός χρυσός 24kt, εφαρμοσμένος ηλεκτρολυτικά σε πυρήνα ανοξείδωτου χάλυβα ακριβείας — με βάρος, λάμψη και αντοχή στις γρατσουνιές."
      },
      {
        icon: Sparkles,
        title: "Ενσωματωμένη επανεπικοινωνία με AI",
        desc: "Κάθε άγγιγμα καταγράφεται στο Banana CRM, με εμπλουτισμένα στοιχεία, έτοιμα για μηνύματα επανεπικοινωνίας που συντάσσει το AI."
      },
      {
        icon: CreditCard,
        title: "Βαθιά χάραξη της εταιρικής σας ταυτότητας",
        desc: "Το λογότυπο, το όνομα και τα στοιχεία σας χαράσσονται μόνιμα στον μεταλλικό πυρήνα — διακρίνονται μέσα από την επιχρύσωση και αποπνέουν αδιαμφισβήτητη ποιότητα."
      },
      {
        icon: Users,
        title: "Για ιδρυτές και στελέχη",
        desc: "Η κάρτα που μαγνητίζει τα βλέμματα στις αίθουσες συνεδριάσεων — για όσους κλείνουν τις μεγαλύτερες συμφωνίες."
      },
      {
        icon: Truck,
        title: "Δωρεάν αποστολή παγκοσμίως",
        desc: "Παραγγελίες άνω των $50 αποστέλλονται δωρεάν σε περισσότερες από 100 χώρες — σε 3–5 εργάσιμες ημέρες."
      },
      {
        icon: BarChart3,
        title: "Στατιστικά σε πραγματικό χρόνο",
        desc: "Δείτε κάθε άγγιγμα, κλικ και υποψήφιο πελάτη στον πίνακα ελέγχου — και μάθετε ποιες εκδηλώσεις φέρνουν πραγματικά αποτελέσματα."
      }
    ]
  },
  anatomy: {
    eyebrow: "Η κατασκευή της κάρτας",
    heading: {
      pre: "Κάθε λεπτομέρεια,",
      em: "μελετημένη."
    },
    image: goldFeatures,
    imageAlt: "Κατασκευή της χρυσής επαγγελματικής κάρτας 24kt με AI — χαραγμένο λογότυπο, τσιπ NFC και κωδικός QR",
    items: [
      {
        t: "Επιφάνεια με επιχρύσωση 24kt",
        d: "Πραγματικός χρυσός 24kt, εφαρμοσμένος ηλεκτρολυτικά για βαθιά λάμψη — με αντοχή στον χρόνο και πολυτελή αίσθηση."
      },
      {
        t: "Πυρήνας ανοξείδωτου χάλυβα",
        d: "Ατσάλι κομμένο με ακρίβεια κάτω από τον χρυσό — χαρίζει στην κάρτα στιβαρό βάρος και αντοχή στην κάμψη."
      },
      {
        t: "Ενσωματωμένο τσιπ NFC",
        d: "Αγγίζετε την κάρτα σε οποιοδήποτε σύγχρονο iPhone ή Android και μοιράζεστε ολόκληρο το προφίλ σας — χωρίς εφαρμογή."
      },
      {
        t: "Τυπωμένος κωδικός QR",
        d: "Η ιδανική εναλλακτική για παλαιότερες συσκευές ή πιο χοντρές θήκες κινητών — για να μη χάνεται καμία γνωριμία."
      }
    ]
  },
  profile: {
    eyebrow: "Το προφίλ σας, όπως το θέλετε",
    heading: {
      pre: "Μία κάρτα.",
      em: "Απεριόριστες ταυτότητες."
    },
    body: "Επιλέξτε διάταξη, προσθέστε τα χρώματα, τη φωτογραφία, τους συνδέσμους, τις υπηρεσίες και τα κουμπιά δράσης σας — και αλλάξτε ανάμεσα σε επαγγελματικό, επενδυτικό ή VIP προφίλ, με την ίδια χρυσή κάρτα.",
    bullets: [
      "Πάνω από 12 επαγγελματικά σχεδιασμένες διατάξεις και θέματα",
      "Προσθέστε βίντεο, συλλογή εικόνων, υπηρεσίες και συχνές ερωτήσεις",
      "Πολύγλωσσα προφίλ",
      "Φόρμα συλλογής στοιχείων υποψήφιων πελατών σε κάθε κάρτα"
    ],
    image: goldProfile,
    imageAlt: "Προσαρμόσιμα ψηφιακά προφίλ σε iPhone"
  },
  compare: {
    eyebrow: "Χαρτί ή χρυσός",
    heading: {
      pre: "Οι χάρτινες κάρτες χάνονται.",
      em: "Η χρυσή αξίζει να εκτίθεται."
    },
    sub: "Αξίζετε κάτι που λειτουργεί πραγματικά — και που οι άλλοι θέλουν να κρατήσουν.",
    paperLabel: "Χάρτινες επαγγελματικές κάρτες",
    productLabel: "Εξατομικευμένη χρυσή επαγγελματική κάρτα 24kt με AI",
    paper: [
      "Μοιράζετε χάρτινες κάρτες και ελπίζετε να τις κρατήσουν",
      "Επικοινωνείτε ξανά μόνοι σας, μέρες αργότερα",
      "Ξεχνάτε ποιον συναντήσατε ή τι συζητήσατε",
      "Χωρίς στατιστικά, CRM ή εικόνα των αποτελεσμάτων",
      "Δεν ξεχωρίζετε από τους υπόλοιπους"
    ],
    product: [
      "Ένα άγγιγμα, άμεση αποθήκευση των στοιχείων σας",
      "Αυτόματη, έξυπνη επανεπικοινωνία",
      "Αυτόματη καταγραφή κάθε επαφής",
      "Στατιστικά σε πραγματικό χρόνο για τις εκδηλώσεις που φέρνουν αποτελέσματα",
      "Πολυτέλεια, κύρος, αξέχαστη εντύπωση"
    ]
  },
  steps: {
    eyebrow: "Πώς λειτουργεί",
    heading: {
      pre: "Δεν χρειάζονται εξηγήσεις.",
      em: "Μόνο ένα άγγιγμα."
    },
    sub: "Όλα όσα χρειάζονται, στη διάθεσή τους — αμέσως.",
    stepLabel: (n) => `Βήμα ${n}`,
    items: [
      {
        title: "Μοιραστείτε εύκολα με ένα άγγιγμα",
        desc: "Αγγίζετε την κάρτα NFC της Banana σε οποιοδήποτε smartphone, χωρίς να χρειάζεται εφαρμογή. Το προφίλ, οι σύνδεσμοι και το ημερολόγιό σας ανοίγουν αμέσως."
      },
      {
        title: "Σας αποθηκεύουν αμέσως",
        desc: "Σας προσθέτουν απευθείας στις επαφές τους — τα στοιχεία σας μένουν εκεί που πραγματικά τα αναζητούν: στο κινητό τους."
      },
      {
        title: "Λαμβάνετε και τα δικά τους στοιχεία",
        desc: "Κάθε νέος υποψήφιος πελάτης καταγράφεται αυτόματα στον πίνακα ελέγχου Banana και συγχρονίζεται με το CRM σας — χωρίς χειροκίνητη καταχώριση."
      }
    ]
  },
  team: {
    eyebrow: "Για ομάδες",
    heading: {
      pre: "Σχεδιασμένη για",
      em: "ιδρυτές και στελέχη."
    },
    body: "Εξοπλίστε τη διοικητική σας ομάδα ή τους VIP συνεργάτες σας με χρυσές κάρτες, διαχειριστείτε κάθε προφίλ από έναν πίνακα ελέγχου και στείλτε κάθε νέα επαφή απευθείας στο κοινό σας CRM.",
    cta: "Παραγγείλετε για την ομάδα σας",
    image: goldTeam,
    imageAlt: "Πίνακας ελέγχου για ομάδες και επιχειρήσεις",
    teamsHref: "/teams"
  },
  faq: {
    eyebrow: "Συχνές ερωτήσεις",
    heading: {
      pre: "Συχνές",
      em: "ερωτήσεις."
    },
    items: [
      {
        q: "Είναι ο χρυσός πραγματικός χρυσός 24kt;",
        a: "Ναι. Η κάρτα επιχρυσώνεται ηλεκτρολυτικά με πραγματικό χρυσό 24kt πάνω σε πυρήνα ανοξείδωτου χάλυβα κομμένο με ακρίβεια — προσφέροντας το βάθος, το χρώμα και τη λάμψη του συμπαγούς χρυσού, με τη στιβαρότητα του ατσαλιού."
      },
      {
        q: "Πόσο παχύ είναι το στρώμα χρυσού;",
        a: "Οι προδιαγραφές της ηλεκτρολυτικής επιχρύσωσης εξασφαλίζουν επίστρωση αρκετά παχιά ώστε να αντέχει στις συνηθισμένες γρατσουνιές και τη φθορά — μπορείτε να έχετε την κάρτα μαζί σας καθημερινά χωρίς να ανησυχείτε για τη φθορά της επιχρύσωσης."
      },
      {
        q: "Πώς εξατομικεύω τη χρυσή κάρτα μου;",
        a: "Μετά την ολοκλήρωση της παραγγελίας, σας στέλνουμε ένα σύντομο έντυπο με τις σχεδιαστικές σας προτιμήσεις. Ανεβάζετε το λογότυπό σας και επιλέγετε τι θέλετε να χαραχθεί — η ομάδα μας ετοιμάζει την προεπισκόπηση, εσείς εγκρίνετε και εμείς προχωράμε στην παραγωγή. Οι περισσότερες κάρτες αποστέλλονται εντός 7–10 εργάσιμων ημερών από την έγκριση του σχεδίου."
      },
      {
        q: "Λειτουργεί η χρυσή κάρτα μέσα από θήκες κινητών;",
        a: "Ναι. Η ενσωματωμένη κεραία NFC έχει ρυθμιστεί ώστε να λειτουργεί μέσα από τις περισσότερες θήκες σιλικόνης, δέρματος και πλαστικού. Για πολύ χοντρές θήκες ή θήκες με μεταλλική πλάτη, μπορείτε επίσης να μοιραστείτε το προφίλ σας μέσω του τυπωμένου κωδικού QR."
      },
      {
        q: "Ποιες ψηφιακές δυνατότητες περιλαμβάνει η χρυσή κάρτα;",
        a: "Κάθε κάρτα περιλαμβάνει προφίλ banana.cards: απεριόριστους συνδέσμους, πολύγλωσσα προφίλ, φόρμα συλλογής στοιχείων υποψήφιων πελατών, ψηφιακή κάρτα για Apple Wallet και Google Wallet, στατιστικά σε πραγματικό χρόνο και συγχρονισμό με CRM και πρόσβαση σε όλα τα εργαλεία AI μας."
      },
      {
        q: "Πώς λειτουργεί η επανεπικοινωνία με AI;",
        a: "Όταν κάποιος αγγίζει την κάρτα σας, τα στοιχεία του εμπλουτίζονται με εταιρεία, ρόλο και ενδείξεις ενδιαφέροντος και μεταφέρονται στον πίνακα ελέγχου σας. Το AI συντάσσει ένα εξατομικευμένο μήνυμα επανεπικοινωνίας με βάση το πλαίσιο της συνάντησης — εσείς το ελέγχετε, το επεξεργάζεστε και το στέλνετε."
      },
      {
        q: "Είναι η χάραξη μόνιμη;",
        a: "Απολύτως. Η χάραξη γίνεται απευθείας στον ατσάλινο πυρήνα κάτω από την επιχρύσωση — αποτελεί μέρος της ίδιας της κάρτας και καλύπτεται από την εγγύηση εφ’ όρου ζωής για την κάρτα."
      },
      {
        q: "Μπορώ να χρησιμοποιήσω τη χρυσή κάρτα με το υπάρχον CRM μου;",
        a: "Ναι. Η Banana συγχρονίζεται απευθείας με HubSpot, Salesforce, Pipedrive, Notion και Google Contacts, καθώς και με περισσότερα από 50 εργαλεία μέσω Zapier."
      },
      {
        q: "Χρειάζεται η κάρτα μπαταρίες ή φόρτιση;",
        a: "Όχι. Το τσιπ NFC είναι παθητικό — δεν χρειάζεται ποτέ φόρτιση ή μπαταρίες."
      },
      {
        q: "Υπάρχει ψηφιακή εναλλακτική αν δεν έχω μαζί μου την κάρτα;",
        a: "Ναι. Το προφίλ σας έχει μοναδικό URL — μοιραστείτε το μέσω συνδέσμου, QR, υπογραφής email ή ψηφιακής κάρτας στο Apple Wallet ή το Google Wallet."
      }
    ]
  },
  outro: {
    heading: {
      pre: "Δικτυωθείτε με",
      em: "χρυσό στα χέρια σας."
    },
    body: "Παραγγείλετε πριν τις 15:00 και αποστέλλουμε την επόμενη εργάσιμη ημέρα. Δωρεάν αποστολή παγκοσμίως για παραγγελίες άνω των $50.",
    primaryCta: "Αποκτήστε τη χρυσή κάρτα σας",
    secondaryCta: "Δείτε όλα τα προϊόντα",
    shopHref: "/shop"
  },
  buyBox: {
    addToCart: "Προσθήκη στο καλάθι"
  },
  gallery: GALLERY_GOLD_EL
};
